import { request } from './request';
import {
  catchError,
  combineLatest,
  EMPTY,
  filter,
  map,
  startWith,
  switchMap,
  tap,
  throttleTime,
  throwError,
} from 'rxjs';
import { queueNotice } from '../components/Notice';
import { bind } from '@react-rxjs/core';
import { convertToAsyncTaskState } from '../utils/rxjs/task';
import { createSignal } from '@react-rxjs/utils';
import { DateRange } from '../types';
import dayjs from 'dayjs';
import { Vehicle } from './vehicle';
import { dialog$, setReserveDialog } from '../components/ReserveModal/service';

export interface ReservationDescriptor {
  clientId: number;
  driverId: number;
  fromDatetime: string;
  id: number;
  objectId: number;
  toDatetime: string;
  objectName: string;
}

export const [range$, setRange] = createSignal<DateRange>();

const getInitialDateRange = (): DateRange => [
  dayjs().startOf('hour').add(1, 'h').toISOString(),
  dayjs().endOf('day').toISOString(),
];

export const reset = () => setRange(getInitialDateRange());

const validateRange = ([from, to]: DateRange): string | null => {
  if (!from || !to) {
    return 'Date.Range.Error.Required';
  }

  if (from > to) {
    return 'Date.Range.Error.Validity';
  }

  if (Math.abs(dayjs(from).diff(to, 'days')) > 31) {
    return 'Date.Range.Error.Exceeded';
  }

  return null;
};

export const [useDateRangeValidation, dateRangeValidation$] = bind(
  dialog$.pipe(
    filter((open) => open),
    switchMap(() =>
      range$.pipe(
        map((dateRange) => ({ dateRange, error: validateRange(dateRange) })),
        startWith({ dateRange: getInitialDateRange(), error: null })
      )
    )
  )
);

const reservations$ = request<ReservationDescriptor[]>(
  `/gateway/vehicle-booking-service/v20230906/bookings/by-driver`
);

export const [reserveVehicleSignal$, reserveVehicle] = createSignal<number>();
export const [deleteReservationSignal$, deleteReservationSignal] =
  createSignal<number>();

export const reserved$ = dateRangeValidation$.pipe(
  switchMap(({ dateRange: [fromDatetime, toDatetime] }) =>
    reserveVehicleSignal$.pipe(
      switchMap((objectId) =>
        request('/gateway/vehicle-booking-service/v20230906/bookings', {
          method: 'POST',
          body: JSON.stringify({
            objectId,
            fromDatetime,
            toDatetime,
          }),
          headers: { 'Content-Type': 'application/json' },
        }).pipe(
          catchError(() => {
            queueNotice({ message: 'Vehicle.Reserve.Error' });
            setReserveDialog(false);
            return EMPTY;
          }),
          tap(() => {
            queueNotice({
              message: 'Vehicle.Reserve.Success',
              variant: 'success',
              timer: 3000,
            });
            setReserveDialog(false);
          })
        )
      )
    )
  )
);

export const canceled$ = deleteReservationSignal$.pipe(
  switchMap((id) =>
    request(`/gateway/vehicle-booking-service/v20230906/bookings/${id}`, {
      method: 'DELETE',
    }).pipe(
      catchError(() => {
        queueNotice({ message: 'Vehicle.CancelReserve.Error' });
        return EMPTY;
      }),
      tap(() => {
        queueNotice({
          message: 'Vehicle.CancelReserve.Success',
          variant: 'success',
          timer: 3000,
        });
      })
    )
  )
);

export const [useCancelReservation, canceledReservation$] = bind(
  canceled$,
  null
);

export const [useReserveVehicle, reservedVehicle$] = bind(reserved$, null);

export const [useReservations] = bind(
  combineLatest([canceledReservation$, reservedVehicle$]).pipe(
    switchMap(() => reservations$.pipe(convertToAsyncTaskState()))
  ),
  {
    result: null,
    running: true,
    error: null,
  }
);

export const [useAvailableVehicles] = bind(
  dateRangeValidation$.pipe(
    filter(({ error }) => !error),
    throttleTime(300, undefined, { leading: false, trailing: true }),
    switchMap(({ dateRange: [fromDatetime, toDatetime] }) =>
      request<Vehicle[]>(
        `/gateway/vehicle-booking-service/v20230906/bookings/available-objects/?fromDatetime=${fromDatetime}&toDatetime=${toDatetime}`
      ).pipe(
        catchError((e) => {
          queueNotice({
            message: 'Vehicle.Get.Error',
          });
          setReserveDialog(false);
          return throwError(() => e);
        }),
        convertToAsyncTaskState()
      )
    )
  ),
  {
    result: null,
    running: true,
    error: null,
  }
);
